import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { featuredProducts, categories } from '../../mocks/products';

export default function HomePage() {
  const addToCart = (product: any) => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItem = cart.find((item: any) => item.id === product.id);
    
    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      cart.push({ ...product, quantity: 1 });
    }
    
    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460]"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="flex items-center justify-between gap-16">
            <div className="w-full max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-8">
                <i className="ri-flashlight-fill text-yellow-400"></i>
                <span className="text-white text-sm font-medium whitespace-nowrap">New Arrivals</span>
              </div>
              
              <h1 className="text-7xl font-black text-white leading-tight mb-6">
                Premium Gaming<br />
                Consoles & High-Performance<br />
                Computing
              </h1>
              
              <p className="text-lg text-white/80 mb-8 leading-relaxed max-w-xl">
                Discover the latest technology in gaming and computing. From cutting-edge consoles to powerful PCs, we bring you the best in electronics.
              </p>
              
              <div className="flex gap-4">
                <Link
                  to="/products"
                  className="px-8 py-4 bg-white text-black font-semibold rounded-xl hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
                >
                  Shop Now
                </Link>
                <Link
                  to="/products"
                  className="px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-xl hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
                >
                  View Catalog
                </Link>
              </div>
            </div>
            
            <div className="w-full max-w-xl">
              <img
                src="https://public.readdy.ai/ai/img_res/948cd2819f2061f3f1a9f03c7cf97ba6.jpg"
                alt="Gaming Setup"
                className="w-full h-auto rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="py-20 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-4 gap-6">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/products?category=${encodeURIComponent(category.name)}`}
                className="group cursor-pointer"
              >
                <div
                  className="rounded-3xl overflow-hidden h-80 relative transition-transform duration-300"
                  style={{ backgroundColor: category.color }}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-white p-6">
                    <h3 className="text-xl font-bold text-gray-900">{category.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{category.count} Products</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-6xl font-bold text-gray-900 mb-4">
              Discover Your Perfect Setup
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore our curated selection of premium gaming consoles, high-performance computers, and essential accessories
            </p>
          </div>

          <div className="grid grid-cols-3 gap-8" data-product-shop>
            {featuredProducts.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
              >
                <Link to={`/product/${product.id}`} className="block">
                  <div className="aspect-square bg-gray-50 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">
                      {product.category}
                    </p>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{product.name}</h3>
                    <p className="text-sm text-gray-600 mb-4">{product.specs}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-3xl font-black text-[#FF6B6B]">
                        ${product.price}
                      </span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          addToCart(product);
                        }}
                        className="w-10 h-10 flex items-center justify-center bg-black text-white rounded-full hover:bg-[#FF6B6B] transition-colors cursor-pointer"
                        aria-label="Add to cart"
                      >
                        <i className="ri-shopping-cart-line text-lg"></i>
                      </button>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/products"
              className="inline-block px-10 py-4 bg-black text-white font-semibold rounded-xl hover:bg-[#FF6B6B] transition-colors whitespace-nowrap cursor-pointer"
            >
              View All Products
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FF6B6B] text-white rounded-2xl mx-auto mb-4">
                <i className="ri-truck-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Free Shipping</h3>
              <p className="text-sm text-gray-600">On orders over $50</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#4ECDC4] text-white rounded-2xl mx-auto mb-4">
                <i className="ri-shield-check-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Warranty</h3>
              <p className="text-sm text-gray-600">1-3 years coverage</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#F4A261] text-white rounded-2xl mx-auto mb-4">
                <i className="ri-customer-service-2-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">24/7 Support</h3>
              <p className="text-sm text-gray-600">Always here to help</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#95A5A6] text-white rounded-2xl mx-auto mb-4">
                <i className="ri-arrow-go-back-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Easy Returns</h3>
              <p className="text-sm text-gray-600">30-day return policy</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E8E]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-black text-white mb-6">
            Ready to Upgrade Your Setup?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join thousands of satisfied gamers and tech enthusiasts. Get exclusive deals and early access to new releases.
          </p>
          <Link
            to="/products"
            className="inline-block px-12 py-5 bg-white text-[#FF6B6B] font-bold rounded-xl hover:bg-gray-100 transition-colors text-lg whitespace-nowrap cursor-pointer"
          >
            Start Shopping
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
